import { CryptoDigestAlgorithm, digestStringAsync } from 'expo-crypto';
import { Platform } from 'react-native';

import { type LaunchDarklyContext } from '../launch-darkly.types';
import { log } from './logger';

// ─────────────────────────────────────────────────────────────────────────────

/**
 * LD uses custom logic per platform/SDK to generate key for context if it's
 * not explicitly provided.
 *
 * The LD client will automatically set the key to a LaunchDarkly-specific,
 * device-unique string that is consistent between app restarts and device reboots.
 *
 * Web/JS/React
 * @see {@link https://launchdarkly.com/docs/sdk/features/anonymous#javascript}
 *
 *   To create an anonymous context, specify the anonymous property and omit
 *   the key property.
 *
 * Native/React Native
 * @see {@link https://launchdarkly.com/docs/sdk/features/anonymous#react-native}
 *
 *   To create an anonymous context, specify the anonymous property
 *   and set the context key to an empty string.
 */
export function withGeneratedContextKey() {
  return Platform.select({
    default: {},
    native: { key: '' },
  });
}

/**
 * Generates a key for the context by hashing the provided context object
 * if the context needs to be consistent across app restarts.
 */
export async function withGeneratedContextKeyByHash(context: LaunchDarklyContext) {
  const stringifiedContext = JSON.stringify(context);

  try {
    return {
      key: await digestStringAsync(CryptoDigestAlgorithm.SHA256, stringifiedContext),
    };
  } catch (error) {
    log.error(error);

    // Fallback to default key generation if hash generation fails
    return withGeneratedContextKey();
  }
}
